import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { apiErr } from "./util";
import { QuestionsEditor } from "./QuestionsEditor";

function TestRow({ t, reload, onQuestions }) {
  const [f, setF] = useState(t);
  useEffect(() => { setF(t); }, [t]);
  const save = () => api.put(`/testseries/admin/tests/${t.id}`, f).then(() => { toast.success("Test saved"); reload(); }).catch(apiErr);
  const num = (k, l) => <label className="text-[11px] text-muted-foreground space-y-1 block">{l}<Input type="number" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-9" data-testid={`ats-test-${k}-${t.id}`} /></label>;
  return (
    <div className="bg-white border border-border rounded-xl p-4 space-y-3" data-testid={`ats-test-row-${t.id}`}>
      <div className="flex flex-wrap gap-3 items-end">
        <label className="text-[11px] text-muted-foreground space-y-1 block flex-1 min-w-[220px]">Test title<Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="h-9" data-testid={`ats-test-title-${t.id}`} /></label>
        <div className="w-24">{num("duration", "Minutes")}</div><div className="w-24">{num("negative_marking", "Negative")}</div><div className="w-24">{num("attempts_allowed", "Attempts")}</div>
      </div>
      <Textarea rows={2} placeholder="Test-specific instructions (optional)" value={f.instructions || ""} onChange={(e) => setF({ ...f, instructions: e.target.value })} data-testid={`ats-test-instr-${t.id}`} />
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className="text-xs text-muted-foreground">{t.question_count} questions · {t.total_marks} marks</span>
        <label className="flex items-center gap-2"><Switch checked={f.status === "published"} onCheckedChange={(v) => setF({ ...f, status: v ? "published" : "draft" })} data-testid={`ats-test-published-${t.id}`} />Published</label>
        <label className="flex items-center gap-2"><Switch checked={!!f.free_preview} onCheckedChange={(v) => setF({ ...f, free_preview: v })} data-testid={`ats-test-free-${t.id}`} />Free test</label>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => onQuestions(t)} data-testid={`ats-test-questions-${t.id}`}><ListChecks size={14} className="mr-1" />Questions</Button>
        <Button size="sm" className="viriks-navy text-white" onClick={save} data-testid={`ats-test-save-${t.id}`}>Save</Button>
        <Button size="icon" variant="ghost" onClick={() => window.confirm("Delete this test and its questions?") && api.delete(`/testseries/admin/tests/${t.id}`).then(reload).catch(apiErr)} data-testid={`ats-test-del-${t.id}`}><Trash2 size={15} /></Button>
      </div>
    </div>
  );
}

export function TestsManager({ pack }) {
  const [tests, setTests] = useState([]);
  const [title, setTitle] = useState("");
  const [qt, setQt] = useState(null);
  const load = useCallback(() => api.get(`/testseries/admin/packs/${pack.id}/tests`).then(({ data }) => setTests(data)), [pack.id]);
  useEffect(() => { load(); }, [load]);
  const add = () => api.post(`/testseries/admin/packs/${pack.id}/tests`, { title, duration: 30 }).then(() => { setTitle(""); load(); }).catch(apiErr);
  if (qt) return <QuestionsEditor test={qt} onBack={() => { setQt(null); load(); }} />;
  return (
    <div className="space-y-4">
      <div className="flex gap-3"><Input placeholder="New test title (e.g. Full Mock Test 2)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ats-new-test-title" /><Button className="viriks-amber text-white" onClick={add} disabled={!title.trim()} data-testid="ats-new-test-add"><Plus size={15} className="mr-1" />Add test</Button></div>
      {tests.length === 0 ? <p className="text-sm text-muted-foreground">No tests yet.</p> : tests.map((t) => <TestRow key={t.id} t={t} reload={load} onQuestions={setQt} />)}
    </div>
  );
}
